import { cx } from '@/app/lib/utils'
import {
  DEFAULT_CURRENCY,
  FINAL_PRICE_SR_LABEL,
  FRACTION_DIGITS,
  FREE_LABEL,
  FROM_PREFIX,
  ORIGINAL_PRICE_SR_LABEL,
  PRICE_LOCALE,
} from './constants'
import styles from './Price.module.scss'
import type { PriceProps } from './types'

/** Formatea un importe como cadena monetaria localizada. */
function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat(PRICE_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: FRACTION_DIGITS,
    maximumFractionDigits: FRACTION_DIGITS,
  }).format(amount)
}

/**
 * Muestra un precio (importe + divisa) con soporte para variantes
 * de oferta ("offer", con importe original tachado) y "desde" ("from").
 * Componente de presentación puro — renderiza en servidor.
 *
 * Accesibilidad: el contexto de cada importe va como texto, no en
 * `aria-label` — en un `<span>` sin rol los lectores de pantalla lo ignoran
 * (WCAG 1.3.1). "Desde" se lee tal cual; en la oferta, «Precio original» y
 * «Precio final» van visualmente ocultos.
 */
export function Price({
  amount,
  currency = DEFAULT_CURRENCY,
  variant = 'default',
  originalAmount,
  isFree = false,
  unit,
  showFrom = false,
  accentColor = false,
  alignment = 'left',
  inverse = false,
  size = 'm',
  className,
}: PriceProps) {
  const formattedAmount = isFree ? FREE_LABEL : formatPrice(amount, currency)
  const formattedOriginal = originalAmount != null ? formatPrice(originalAmount, currency) : null
  const withFrom = variant === 'from' || showFrom
  const isOffer = variant === 'offer'

  return (
    <div
      className={cx(
        styles.root,
        styles[alignment],
        inverse && styles.inverse,
        size === 'l' && styles.sizeL,
        className,
      )}
    >
      {/* Original tachado a la izquierda del importe final, en la misma fila
          (paridad con la referencia, `.priceGroup`/`.priceLeft`) — solo
          envuelve a una segunda línea si el ancho disponible no llega a
          caber todo en una. */}
      <div className={cx(styles.priceGroup, isOffer && styles.priceGroupOffer)}>
        {isOffer && formattedOriginal ? (
          <span className={styles.priceLeft}>
            {/* En la oferta "Desde" acompaña visualmente al tachado, pero se
                lee con el importe final («Precio final desde …»). */}
            {withFrom && (
              <span aria-hidden="true" className={styles.fromOffer}>
                {FROM_PREFIX}
              </span>
            )}
            <span className={styles.original}>
              <span className={styles.srOnly}>{ORIGINAL_PRICE_SR_LABEL} </span>
              {formattedOriginal}
            </span>
          </span>
        ) : (
          withFrom && <span className={styles.from}>{FROM_PREFIX}</span>
        )}
        <span
          className={cx(
            styles.amount,
            isOffer && (accentColor ? styles.offerAccent : styles.offer),
          )}
        >
          {isOffer && formattedOriginal && (
            <span className={styles.srOnly}>
              {FINAL_PRICE_SR_LABEL}
              {withFrom ? ` ${FROM_PREFIX.toLowerCase()}` : ''}{' '}
            </span>
          )}
          {formattedAmount}
        </span>
      </div>

      {unit && <span className={styles.unit}>{unit}</span>}
    </div>
  )
}
